import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  TextareaWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  isValidDate,
} from 'shared-components';

import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { IDate } from '../../../../../types';
import { useNavigate } from 'react-router-dom';

const VisaPrepFormUniEnrolmentPage1 = (_props: any) => {
  // dispatch actions
  const dispatch = useDispatch();

  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE7_1));
  };

  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const visaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);
  const {
    hasReceivedCas,
    studiesConfirmationNumber,
    courseEndDate: storeCourseEndDate,
    alreadyStartedStudying,
    studyAddress,
    assessedBySponsor,
  } = visaPrepForm;

  // constants
  const currentStep = 1;
  const steps = 3;

  // component state
  const [complete, setComplete] = useState(false);
  const [courseEndDate, setCourseEndDate] = useState<IDate>(() => getDateFromString(storeCourseEndDate));
  const [courseEndDateError, setCourseEndDateError] = useState('');

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasReceivedCas,
      studiesConfirmationNumber,
      courseEndDate: storeCourseEndDate,
      alreadyStartedStudying,
      studyAddress,
      assessedBySponsor,
      courseEndDateError,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasReceivedCas,
    studiesConfirmationNumber,
    storeCourseEndDate,
    alreadyStartedStudying,
    studyAddress,
    assessedBySponsor,
    courseEndDateError,
  ]);

  const validateDate = (dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        setCourseEndDateError(`Doesn't look like a valid date`);
      } else {
        onChange(VQ.COURSE_END_DATE, getStringFromDate(dateValue));
        setCourseEndDateError('');
      }
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'University Enrolment'}
    >
      <RadioOptionsWithConfirm
        key={VQ.HAS_RECEIVED_CAS}
        id={VQ.HAS_RECEIVED_CAS}
        label={'Have you received your CAS yet?'}
        onClick={(question: IQuestion) => onChange(VQ.HAS_RECEIVED_CAS, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasReceivedCas === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasReceivedCas === false,
          },
        ]}
      />

      {hasReceivedCas === true ? (
        <InputWithConfirm
          key={VQ.STUDIES_CONFIRMATION_NUMBER}
          id={VQ.STUDIES_CONFIRMATION_NUMBER}
          value={studiesConfirmationNumber}
          label={'What is your Confirmation of Acceptance for Studies Number?'}
          onChange={(evt: any) => {
            onChange(VQ.STUDIES_CONFIRMATION_NUMBER, evt.currentTarget.value);
          }}
        />
      ) : null}

      {hasReceivedCas === true ? (
        <DateInputWithConfirm
          key={VQ.COURSE_END_DATE}
          id={VQ.COURSE_END_DATE}
          label={'Course end date'}
          value={courseEndDate}
          errorText={courseEndDateError}
          onChangeDD={(evt: any) => {
            setCourseEndDate({
              ...courseEndDate,
              DD: evt.currentTarget.value,
            });
          }}
          onChangeMM={(evt: any) => {
            setCourseEndDate({
              ...courseEndDate,
              MM: evt.currentTarget.value,
            });
          }}
          onChangeYY={(evt: any) => {
            const { value } = evt.currentTarget;
            if (value.length === 4) {
              setCourseEndDate({
                ...courseEndDate,
                YYYY: value,
              });

              validateDate({
                ...courseEndDate,
                YYYY: value,
              });
            } else {
              setCourseEndDate({
                ...courseEndDate,
                YYYY: value,
              });
            }
          }}
          onBlur={(_evt: any) =>
            validateDate({
              ...courseEndDate,
            })
          }
        />
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.ALREADY_STARTED_STUDYING}
        id={VQ.ALREADY_STARTED_STUDYING}
        label={'Have you already started studying this course?'}
        onClick={(question: IQuestion) => onChange(VQ.ALREADY_STARTED_STUDYING, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: alreadyStartedStudying === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: alreadyStartedStudying === false,
          },
        ]}
      />

      {hasReceivedCas === true ? (
        <TextareaWithConfirm
          key={VQ.STUDY_ADDRESS}
          id={VQ.STUDY_ADDRESS}
          value={studyAddress}
          label={'What is the address of the main site of study?'}
          onChange={(evt: any) => onChange(VQ.STUDY_ADDRESS, evt.currentTarget.value)}
        />
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.ASSESSED_BY_SPONSOR}
        id={VQ.ASSESSED_BY_SPONSOR}
        label={
          'Have you been assessed by the sponsor by other means, for example references or a portfolio of artwork?'
        }
        onClick={(question: IQuestion) => onChange(VQ.ASSESSED_BY_SPONSOR, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: assessedBySponsor === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: assessedBySponsor === false,
          },
        ]}
      />
    </QuestionPage>
  );
};
export default VisaPrepFormUniEnrolmentPage1;
